/**
 * workflow:metrics command - Get workflow metrics
 */

import { defineCommand, handleError, type PluginContextV3 , type CommandResult} from '@kb-labs/sdk';
import { type MetricsFlags } from '@kb-labs/workflow-contracts';
import { WorkflowDaemonClient } from '../http-client.js';

type MetricsInput = MetricsFlags & { argv?: string[]; flags?: MetricsFlags };

export default defineCommand<unknown, MetricsInput, unknown>({
  id: 'workflow:metrics',
  description: 'Get workflow daemon metrics',

  handler: {
    async execute(ctx: PluginContextV3, input: MetricsInput): Promise<CommandResult> {
      const flags = input.flags ?? input;
      const outputJson = flags.json ?? false;

      try {
        const client = new WorkflowDaemonClient();
        const metrics = await client.getMetrics();

        if (outputJson) {
          ctx.ui?.json?.({ ok: true, data: metrics });
        } else {
          const runsItems = [
            `Total: ${metrics.runs.total}`,
            `Queued: ${metrics.runs.queued}`,
            `Running: ${metrics.runs.running}`,
            `Completed: ${metrics.runs.completed}`,
            `Failed: ${metrics.runs.failed}`,
            `Cancelled: ${metrics.runs.cancelled}`,
          ];

          const jobsItems = [
            `Total: ${metrics.jobs.total}`,
            `Queued: ${metrics.jobs.queued}`,
            `Running: ${metrics.jobs.running}`,
            `Completed: ${metrics.jobs.completed}`,
            `Failed: ${metrics.jobs.failed}`,
          ];

          ctx.ui?.success?.('Workflow Metrics', {
            title: 'Workflow Daemon',
            sections: [
              { header: 'Runs', items: runsItems },
              { header: 'Jobs', items: jobsItems },
            ],
          });
        }

        return { ok: true };
      } catch (error) {
        if (outputJson) {
          handleError(ctx, error, true);
        } else {
          const message = error instanceof Error ? error.message : String(error);
          ctx.ui?.error?.(message, { hint: 'Start with: kb-dev start workflow' });
        }
        return { ok: false, error: 'Command failed' };
      }
    },
  },
});
